/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import type {DisplayField} from '@/lib/classes/InfoWindowContent';

/**
 * Configuration for linking documents within the Info Window.
 */
interface LinkingConfig {
  /** The name of the Firestore collection containing the linkable documents. */
  firestoreCollection: string;
  /** The name of the local filter collection containing the linkable documents. */
  filterCollection: string;
  /**
   * An array of Display Field labels from the linked document to display in the Info Window. If not provided, all
   * fields will be displayed.
   */
  fields?: string[];
  /** Optional, if true, indicates that the Info Window is for an OM item. */
  isOmInfoWindow?: boolean;
  /** Optional, The action to use to create a new tab in the Info Window when a linked document is clicked. */
  iwAction?: Function;
  /**
   * Optional, a function to execute when a linked document is clicked, given the document's item from the filter
   * collection (`undefined` when the collection does not hold it) and the filter collection's name.
   */
  linkAction?: Function;
  /** Optional, called after a linked document is unlinked, with the unlinked document's id. */
  onUnlink?: (id: string) => void;
  /** Optional, called when an unlink is undone, with the re-linked document's id. */
  onUndoUnlink?: (id: string) => void;
  /** Gets the text that should be displayed as the header suffix. */
  getHeaderSuffix: (displayFields: DisplayField[]) => string | null;
  /**
   * Optional, a function to transform the value of the field used for the section header suffix.
   * If not provided, the value will be used as is.
   */
  transformSuffix?: (value: string) => string;
}

/**
 * @param fieldsToDisplay An array of Display Field labels from the linked document to display in the Info Window.
 * @param firestoreCollection The name of the Firestore collection containing the linkable documents.
 * @param filterCollection The name of the local filter collection containing the linkable documents.
 * @param isOmInfoWindow Optional, if true, indicates that the Info Window is for an OM item.
 * @param iwAction The action to use to create a new tab in the Info Window when a linked document is clicked.
 * @param linkAction Optional, a function to execute when a linked document is clicked.
 * @param sectionHeaderSuffixField Optional, the label of a field from the linked document to use as a suffix for the
 * section header.
 * @param transformSuffix Optional, a function to transform the value of the field used for the section header suffix.
 */
const createLinkingConfig = (config: {
  firestoreCollection: string;
  filterCollection: string;
  isOmInfoWindow?: boolean;
  fieldsToDisplay?: string[];
  iwAction?: Function;
  linkAction?: Function;
  onUnlink?: (id: string) => void;
  onUndoUnlink?: (id: string) => void;
  sectionHeaderSuffixField?: string;
  transformSuffix?: (value: string) => string;
}): LinkingConfig => {
  return {
    firestoreCollection: config.firestoreCollection,
    filterCollection: config.filterCollection,
    isOmInfoWindow: config.isOmInfoWindow || false,
    fields: config.fieldsToDisplay,
    iwAction: config.iwAction,
    linkAction: config.linkAction,
    onUnlink: config.onUnlink,
    onUndoUnlink: config.onUndoUnlink,
    getHeaderSuffix: (displayFields: DisplayField[]) => {
      if (!displayFields || displayFields.length === 0) return null;

      const df = displayFields.find(({label}) => label === config.sectionHeaderSuffixField);
      if (!df || !df.value) return null;

      return config.transformSuffix ? config.transformSuffix(df.value as string) : `${df.value}`;
    },
  };
};

export {
  LinkingConfig,
  createLinkingConfig,
};
